// App
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

// Actions
import AdActions from 'app/shared/flux/actions/AdActions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Components
import AreaPage from 'app/shared/pages/AreaPage';
import FilterNav from 'app/shared/modules/navigation/FilterNav';

// Misc / Utils
import constants from 'app/shared/constants/ConstantsBundle';
import controller from 'app/shared/wrappers/FetchAreaWrapper/controller';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';

// Lodash
import isEmpty from 'lodash/isEmpty';

// serverRouteWillMount functions
import loadGeolocation from 'app/shared/wrappers/FetchAreaWrapper/loadGeolocation';
import loadAreaPage from 'app/shared/wrappers/FetchAreaWrapper/loadAreaPage';
import resolveFetchListingsPromises from 'app/shared/wrappers/FetchAreaWrapper/resolveFetchListingsPromises';
import loadAreaSeoLinks from 'app/shared/wrappers/FetchAreaWrapper/loadAreaSeoLinks';
import loadFilterFromUrlAndApi from 'app/shared/wrappers/FetchAreaWrapper/loadFilterFromUrlAndApi';
import { emitTrackViewSrpEvent } from 'app/shared/models/Clickstream/PageViewEvents';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';

// Types
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { RouteComponentProps } from 'react-router-dom';

const logger = getGlobalLogger('wrappers/fetchArea');

// Type definitions
interface Area {
  resourceId?: string;
  type?: string;
}

interface Geolocation {
  resourceId?: string;
  area?: Area;
}

interface LocationQuery {
  page?: number;
  [key: string]: string | number | boolean | undefined;
}

interface LocationCurrentState {
  current?: {
    query: LocationQuery;
  };
}

interface Trace {
  startAsyncSpan: (params: { marker: string }) => void;
  finishAsyncSpan: (params: { marker: string }) => void;
}

interface ReduxStore {
  getState: () => RootReduxState;
  dispatch: ThunkDispatch<RootReduxState, undefined, AnyAction>;
}

interface RouteState {
  pathname: string;
  query: Record<string, string | number | boolean | undefined>;
}

export interface ReactContext {
  store: ReduxStore;
  trace: Trace;
  state: RouteState;
  ip: string;
}

interface ServerResponse {
  error?: boolean;
  to?: string;
  changeHttpStatus?: number;
  area?: Area;
  [key: string]: unknown;
}

export interface FetchAreaWrapperProps {
  advancedFilterVisible: boolean;
  area: Area;
  currentPage: number;
  dispatch: ThunkDispatch<RootReduxState, undefined, AnyAction>;
  geolocation: Geolocation;
  isClientSideLoadedPage: boolean;
  isInitialSsrPage: boolean;
  isMobile: boolean;
  listings: unknown;
  listingsLoading: boolean;
  totalBuildings: number;
  isMapPanning: boolean;
  location: LocationCurrentState;
  pathname: string;
  query: Record<string, string | number | boolean | undefined>;
  // From react-router
  history: RouteComponentProps['history'];
  match: RouteComponentProps['match'];
}

class FetchAreaWrapper extends React.Component<FetchAreaWrapperProps> {
  additionalDataTimer: ReturnType<typeof setTimeout> | null;
  TIMEOUT_LENGTH_MS: number;

  constructor(props: FetchAreaWrapperProps) {
    super(props);

    this.additionalDataTimer = null;
    this.TIMEOUT_LENGTH_MS = 2000;
  }

  componentDidMount() {
    const { area, dispatch, geolocation, isClientSideLoadedPage, isMobile, pathname, query } = this.props;

    const { areaResourceId } = controller.getAreaSeoInfo(pathname);
    // current area does not match the url
    // clear the current area before fetching new one
    // this acts as an "area loading" mechanism (shows skeleton)
    if (!routeUtils.isNearMeUrl(areaResourceId) && areaResourceId !== area.resourceId) {
      dispatch(AreaActions.clearCurrentArea());
    }

    const hasLatAndLon = routeUtils.hasLatAndLon(query);
    const areaMustBeDerivedFromMap = !isMobile && hasLatAndLon;

    const isMapView = !isMobile; // on mWeb, the area page defaults to list view
    const isNearMeUrl = routeUtils.isNearMeUrl(areaResourceId);
    const geoOrAreaResourceId = isNearMeUrl && !isEmpty(geolocation) ? geolocation.resourceId : areaResourceId;

    if (isClientSideLoadedPage || areaMustBeDerivedFromMap) {
      dispatch(AreaActions.loadAreaPage(geoOrAreaResourceId, isMapView))
        .then((res: ServerResponse) => {
          if (res === Object(res) && res.error && res.to) {
            return res;
          }

          return true;
        })
        .then((res: ServerResponse | boolean) => {
          if (res === Object(res) && (res as ServerResponse).error && (res as ServerResponse).to) {
            return (window.router as { transitionTo: (path: string) => void }).transitionTo(
              (res as ServerResponse).to as string,
            );
          }

          const forceAdRefresh = true;
          dispatch(AdActions.refreshAd(forceAdRefresh));

          return this.setAdditionalDataTimer();
        })
        .catch((error: Error) => {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'component.fetchAreaWrapper.componentDidMount#loadAreaPage',
              errorClass: 'areaActions',
            }),
          );
        });
    } else {
      emitTrackViewSrpEvent();
    }
  }

  componentDidUpdate(prevProps: FetchAreaWrapperProps) {
    const { currentPage } = this.props;

    // track page view changes in analytics
    if (prevProps.currentPage && currentPage && prevProps.currentPage !== currentPage) {
      emitTrackViewSrpEvent();
    }
  }

  componentWillUnmount() {
    this.cancelAdditionalDataTimer();
  }
  serverRouteWillMount(reactContext: ReactContext): Promise<unknown> {
    const { store } = reactContext;
    const { pathname } = reactContext.state;
    const state = store.getState();

    const { areaResourceId } = controller.getAreaSeoInfo(pathname);
    const trace = reactContext.trace;
    const fetchData: Array<Promise<unknown>> = [];
    const fetchAsyncAreaData = (resourceId = 'new-york-ny'): Promise<unknown> => {
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#fetchDataPromiseArray' });
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#AreaActions#loadAreaPage' });
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#FilterActions#loadFilterFromUrlAndApi' });

      const beforeFetchListings: Array<Promise<unknown>> = [];
      // fetchListings relies on the `filter` and `area`
      beforeFetchListings.push(
        loadFilterFromUrlAndApi(reactContext),
        loadAreaPage(reactContext, { areaResourceId: resourceId }),
      );

      fetchData.push(
        resolveFetchListingsPromises(
          reactContext,
          beforeFetchListings as unknown as Array<PromiseLike<ServerResponse>>,
        ),
      );

      // note: never called - waiting on V3
      // trace.startAsyncSpan({ marker: 'FetchAreaWrapper#AreaActions#loadAreaBlogPosts' });
      // fetchData.push(loadAreaBlogPosts(reactContext, { areaResourceId: resourceId }));

      // Only fetch SEO footer links if resourceId is defined to prevent /undefined/ URLs
      if (resourceId) {
        trace.startAsyncSpan({ marker: 'FetchAreaWrapper#AreaActions#loadAreaSeoLinks' });
        fetchData.push(loadAreaSeoLinks(reactContext, { areaResourceId: resourceId }));
      } else {
        logger?.error('Missing resourceId in FetchAreaWrapper#serverRouteWillMount before calling loadAreaSeoLinks', {
          pathname,
          resourceId,
          areaResourceId,
        });
      }

      return Promise.all(fetchData)
        .then((responses: Array<unknown> = []) => {
          trace.finishAsyncSpan({ marker: 'FetchAreaWrapper#fetchDataPromiseArray' });

          return responses;
        })
        .catch((error: Error) => {
          logger?.warn({
            error,
            msg: 'AreaActions.loadAreaPage#Promise.all',
            pathname,
            resourceId: areaResourceId,
            requestId: state.app.requestId,
          });
        });
    };

    if (routeUtils.isNearMeUrl(areaResourceId)) {
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#loadGeolocationData' });
      return loadGeolocation(reactContext).then((geoData: unknown) => {
        // IP address might be out of range for `area/byIp` endpoint
        if (geoData === false) {
          logError({
            error: 'FetchAreaWrapper.serverRouteWillMount.geoPromise',
            errorType: 'FetchAreaWrapperError',
            context: `No geocode for IP ${reactContext.ip}`,
          });
          return fetchAsyncAreaData();
        }
        const geoDataTyped = geoData as ServerResponse;
        const geoDataId = geoDataTyped.area && geoDataTyped.area.resourceId;

        return fetchAsyncAreaData(geoDataId);
      });
    }

    return fetchAsyncAreaData(areaResourceId);
  }
  // delay getting more information about the area
  // if the user got here via client side transition
  // until they have been on the page for this.TIMEOUT_LENGTH_MS
  setAdditionalDataTimer = () => {
    this.additionalDataTimer = setTimeout(this.getAdditionalAreaData, this.TIMEOUT_LENGTH_MS);
  };

  cancelAdditionalDataTimer = () => {
    if (this.additionalDataTimer) {
      clearTimeout(this.additionalDataTimer);
    }
  };

  getAdditionalAreaData = () => {
    const { dispatch, geolocation, pathname } = this.props;
    const { areaResourceId, searchSlug } = controller.getAreaSeoInfo(pathname);
    const isNearMeUrl = routeUtils.isNearMeUrl(areaResourceId);
    const geoOrAreaResourceId = isNearMeUrl && !isEmpty(geolocation) ? geolocation.resourceId : areaResourceId;

    const maxBuildings = this.props.totalBuildings;
    const maxPage = Math.max(Math.ceil(maxBuildings / constants.MAX_LISTINGS_PER_PAGE), 1);
    const currentPage = this.props.location.current && this.props.location.current.query.page;
    const onLastPage = maxPage === Number(currentPage);

    if (onLastPage) {
      dispatch(ListingEngineActions.fetchNearbyAreasListings(geoOrAreaResourceId)).catch((error: Error) => {
        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.fetchAreaWrapper.componentDidMount#fetchNearbyAreasListings',
            errorClass: 'listingEngineActions',
          }),
        );
      });
    }

    // Guard: Only fetch SEO footer links if geoOrAreaResourceId is defined to prevent /undefined/ URLs
    if (!geoOrAreaResourceId) {
      logger?.error(
        'Missing geoOrAreaResourceId in FetchAreaWrapper#getAdditionalAreaData before calling loadAreaSeoLinks',
        {
          pathname,
          areaResourceId,
          geoOrAreaResourceId,
          searchSlug,
          isNearMeUrl,
          hasGeolocation: !isEmpty(geolocation),
        },
      );
    } else {
      dispatch(AreaActions.loadAreaSeoLinks({ resourceId: geoOrAreaResourceId, searchSlug })).catch((error: Error) => {
        logger?.error('Error loading SEO links in getAdditionalAreaData', {
          error,
          errorLocation: 'component.fetchAreaWrapper.componentDidMount#loadAreaSeoLinks',
          resourceId: geoOrAreaResourceId,
          pathname,
          searchSlug,
        });

        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'component.fetchAreaWrapper.componentDidMount#loadAreaSeoLinks',
            errorClass: 'areaActions',
          }),
        );
      });
    }

    emitTrackViewSrpEvent();

    this.additionalDataTimer = null;
  };

  render() {
    const { isClientSideLoadedPage, listingsLoading, area } = this.props;
    const areaIsLoading = isClientSideLoadedPage ? !area.resourceId : false;
    const showSkeletonPage = (areaIsLoading || listingsLoading) && isClientSideLoadedPage;

    return (
      <>
        <FilterNav />
        <AreaPage showSkeletonPage={showSkeletonPage} />
      </>
    );
  }
}

const mapStateToProps = (state: RootReduxState, ownProps: RouteComponentProps) => ({
  advancedFilterVisible: state.app.advancedFilterVisible,
  area: state.area.area,
  currentPage: Number(state.location?.current?.query?.page) || 1,
  geolocation: state.geolocation.area,
  isClientSideLoadedPage: state.app.isClientSideLoadedPage,
  isInitialSsrPage: state.app.isInitialSsrPage,
  isMobile: state.app.device.screenWidth === 'sm',
  listings: state.listings.listingGroups.byCoords,
  listingsLoading: !state.app.fetchListingsByCoordsComplete,
  totalBuildings: state.listings.totalBuildings,
  isMapPanning: state.app.isMapPanning,
  location: state.location as unknown as LocationCurrentState,
  pathname: ownProps.location.pathname,
  query: queryUtils.parse(ownProps.location.search),
  history: ownProps.history,
  match: ownProps.match,
});

export default withRouter(connect(mapStateToProps)(FetchAreaWrapper));
